/* ==========================================================================
   POTSTILL PAGE ENGINE — "What is potstill brandy?" (page.potstill-brandy.json)
   Loaded as a module by sections/psb-still.liquid. Self-contained: it reads the
   theme's GSAP / ScrollTrigger / Lenis if present, but owns every behaviour here.
   All copy is server-rendered; this file only animates and scores what's there.
   Three.js is imported lazily ('three' resolves via the layout's importmap) and only
   when hardware WebGL exists and motion is allowed, after load + idle; otherwise the
   engraved SVG still stays.
   ========================================================================== */

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const lerp = (a, b, t) => a + (b - a) * t;
const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* Wait for DOMContentLoaded, not merely readyState "interactive". This module runs as a
   deferred script BEFORE the theme's deferred gsap/ScrollTrigger/Lenis tags (it sits
   earlier in the document), at which point readyState already reads "interactive". The old
   check resolved there and read window.gsap as undefined, so every GSAP flourish silently
   fell back to its no-motion path (found 25 Sep 2026). `load` covers a late (editor) load. */
await new Promise((r) => {
  if (document.readyState === "complete") { r(); return; }
  addEventListener("DOMContentLoaded", r, { once: true });
  addEventListener("load", r, { once: true });
});

const gsap = window.gsap;
const ST = window.ScrollTrigger;
if (gsap && ST) gsap.registerPlugin(ST);

/* Wait for the theme's page-load curtain (.curtain) to leave, as the theme's own
   entrances do, so nothing plays unseen behind it. Hard timeout: never hide content. */
function afterCurtain(fn) {
  const c = $(".curtain");
  if (!c || !c.isConnected) { fn(); return; }
  let done = false;
  const go = () => { if (done) return; done = true; mo.disconnect(); fn(); };
  const mo = new MutationObserver(() => { if (!c.isConnected) go(); });
  mo.observe(document.body, { childList: true });
  setTimeout(go, 4000);
}

/* The colour of brandy by years in oak, water-white to mahogany. Shared by the barrel
   slider and the brandy discovery, so both sections age the spirit the same way. */
const RAMP = [[0, [241, 234, 212]], [2, [236, 216, 154]], [4, [224, 182, 96]], [7, [201, 138, 58]], [11, [164, 94, 34]], [16, [110, 53, 20]], [20, [77, 35, 12]], [30, [55, 24, 8]]];
function spiritAt(y) {
  for (let i = 1; i < RAMP.length; i++) {
    if (y <= RAMP[i][0]) {
      const [y0, c0] = RAMP[i - 1], [y1, c1] = RAMP[i], k = (y - y0) / (y1 - y0);
      return `rgb(${c0.map((c, j) => Math.round(lerp(c, c1[j], k))).join(",")})`;
    }
  }
  return "rgb(55,24,8)";
}

/* ---------- reveals ---------- */
function initReveals() {
  const els = $$(".psb [data-psb-reveal]");
  if (REDUCED || !("IntersectionObserver" in window)) { els.forEach((e) => e.classList.add("is-in")); return; }
  afterCurtain(() => {
    const io = new IntersectionObserver((ents) => ents.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add("is-in"); io.unobserve(en.target); }
    }), { rootMargin: "0px 0px -8% 0px", threshold: 0.12 });
    els.forEach((e) => io.observe(e));
  });
}

/* ---------- hero title: word rise ---------- */
function initTitle() {
  const h = $("[data-psb-split]");
  if (!h || REDUCED || !gsap) return;
  const wrapWords = (node) => {
    Array.from(node.childNodes).forEach((n) => {
      if (n.nodeType === 3) {
        const frag = document.createDocumentFragment();
        n.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
          const w = document.createElement("span"); w.className = "psb-w";
          const i = document.createElement("span"); i.className = "psb-w__i"; i.textContent = part;
          w.appendChild(i); frag.appendChild(w);
        });
        n.replaceWith(frag);
      } else if (n.nodeType === 1) wrapWords(n);
    });
  };
  wrapWords(h);
  const inner = $$(".psb-w__i", h);
  gsap.set(inner, { yPercent: 115, rotate: 4 });
  afterCurtain(() => gsap.to(inner, { yPercent: 0, rotate: 0, duration: 1.1, ease: "expo.out", stagger: 0.06, onComplete: () => h.classList.add("is-risen") }));
}

/* ---------- "brandewijn → burnt wine → brandy" scramble ---------- */
function initMorph() {
  const el = $("[data-psb-morph]");
  if (!el) return;
  const words = el.dataset.psbMorph.split("|").map((w) => w.trim()).filter(Boolean);
  const span = el.firstElementChild;
  if (REDUCED || words.length < 2) { span.textContent = words[words.length - 1]; return; }
  const glyphs = "abcdefghijklmnopqrstuvwxyzæøåij";
  let running = false;
  const scramble = (from, to, dur) => new Promise((res) => {
    const len = Math.max(from.length, to.length), t0 = performance.now();
    const tick = (now) => {
      const p = clamp((now - t0) / dur, 0, 1);
      let out = "";
      for (let i = 0; i < len; i++) {
        const settle = (i / len) * 0.6 + 0.4;
        if (p >= settle) out += to[i] || "";
        else if (p > settle - 0.45) out += to[i] === " " ? " " : glyphs[(Math.random() * glyphs.length) | 0];
        else out += from[i] || "";
      }
      span.textContent = out;
      p < 1 ? requestAnimationFrame(tick) : res();
    };
    requestAnimationFrame(tick);
  });
  const play = async () => {
    if (running) return; running = true;
    span.textContent = words[0];
    for (let i = 1; i < words.length; i++) {
      await new Promise((r) => setTimeout(r, 1100));
      await scramble(words[i - 1], words[i], 900);
    }
    running = false;
  };
  new IntersectionObserver((ents) => ents.forEach((en) => { if (en.isIntersecting) play(); }), { threshold: 0.6 }).observe(el);
}

/* ==========================================================================
   THE STILL — sticky three.js alembic (or the engraved SVG when it can't run)
   ========================================================================== */
/* Hardware WebGL only. A software rasteriser (SwiftShader / llvmpipe: headless Chrome, so
   Googlebot, PageSpeed and GSC's live test, plus GPU-less machines) spent ~10 s of main
   thread drawing the still every frame, and Google's tools timed out on the page (7 Oct
   2026). failIfMajorPerformanceCaveat refuses software contexts; the renderer name catches
   any that slip through. Those visitors keep the engraved SVG. */
function hasWebGL() {
  try {
    const c = document.createElement("canvas");
    const opts = { failIfMajorPerformanceCaveat: true };
    const gl = c.getContext("webgl2", opts) || c.getContext("webgl", opts);
    if (!gl) return false;
    const info = gl.getExtension("WEBGL_debug_renderer_info");
    const name = String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER));
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return !/swiftshader|llvmpipe|softpipe|software|basic render/i.test(name);
  } catch (e) { return false; }
}

/* Build the 3D only once the page has loaded and the main thread is free, so it never
   competes with first paint; the SVG still shows until then. */
const whenIdle = () => new Promise((r) => {
  const idle = () => (window.requestIdleCallback ? requestIdleCallback(() => r(), { timeout: 2500 }) : setTimeout(r, 300));
  document.readyState === "complete" ? idle() : addEventListener("load", idle, { once: true });
});

async function initStill() {
  const sec = $("[data-psb-still]");
  if (!sec) return;
  const state = { focus: "all", explode: 0, explodeTarget: 0, heroP: 0, heat: 0, heatTarget: 0 };

  /* Focus: whichever part/word sits on the reading line lights up. */
  const focusEls = $$("[data-psb-part], [data-psb-focus]", sec);
  const svgParts = $$(".psb-still__fallback [data-part]", sec);
  const labels = $$("[data-label]", sec);
  const MAP = { all: null, still: ["neck", "condenser", "receiver"], pot: ["pot"] };
  const setFocus = (key) => {
    state.focus = key || "all";
    const keys = MAP[state.focus] !== undefined ? MAP[state.focus] : [state.focus];
    svgParts.forEach((p) => p.classList.toggle("is-dim", !!keys && !keys.includes(p.dataset.part)));
    labels.forEach((l) => l.classList.toggle("is-on", !!keys && keys.includes(l.dataset.label)));
    $$("[data-psb-part]", sec).forEach((el) => el.classList.toggle("is-on", el.dataset.psbPart === state.focus));
  };
  if ("IntersectionObserver" in window) {
    const live = new Set();
    const io = new IntersectionObserver((ents) => {
      ents.forEach((en) => (en.isIntersecting ? live.add(en.target) : live.delete(en.target)));
      // A specific part on the reading line beats the chapter wrapped around it.
      const on = Array.from(live);
      const cur = on.find((e) => e.dataset.psbPart && e.dataset.psbPart !== "all")
        || on.find((e) => e.dataset.psbFocus && e.dataset.psbFocus !== "all") || on[0];
      setFocus(cur ? (cur.dataset.psbPart || cur.dataset.psbFocus) : "all");
    }, { rootMargin: "-48% 0px -48% 0px" });
    focusEls.forEach((e) => io.observe(e));
  }
  $$("[data-psb-focus]", sec).forEach((el) => {
    el.addEventListener("pointerenter", () => setFocus(el.dataset.psbFocus));
  });

  /* Explode during the anatomy chapter (drives both SVG labels and 3D). */
  const anatomy = $("[data-psb-anatomy]", sec);
  const onScroll = () => {
    const vh = innerHeight;
    if (anatomy) {
      const r = anatomy.getBoundingClientRect();
      state.explodeTarget = clamp((vh * 0.85 - r.top) / (vh * 0.6), 0, 1) * (r.bottom > vh * 0.2 ? 1 : clamp((r.bottom) / (vh * 0.2), 0, 1));
    }
    const sr = sec.getBoundingClientRect();
    state.heroP = clamp(-sr.top / Math.max(1, sr.height - vh), 0, 1);
    // "Scroll to light the still": cold on arrival, alight within half a screen of scrolling.
    state.heatTarget = clamp((-sr.top - vh * 0.04) / (vh * 0.45), 0, 1);
    sec.style.setProperty("--heat", state.heatTarget.toFixed(3));
    sec.classList.toggle("is-exploded", state.explodeTarget > 0.5);
  };
  addEventListener("scroll", onScroll, { passive: true });
  onScroll();

  if (REDUCED || !hasWebGL()) return;
  await whenIdle();
  let THREE;
  try { THREE = await import("three"); } catch (e) { return; }
  buildStill3D(THREE, sec, state, labels);
}

function buildStill3D(THREE, sec, state, labels) {
  const stage = $(".psb-still__stage", sec);
  const canvas = $("[data-psb-gl]", sec);
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: "high-performance" });
  renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 1.75));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.02;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 200);

  /* A painted cellar for the copper to reflect: warm lamps, the fire, one cool window. */
  const envTex = (() => {
    const c = document.createElement("canvas"); c.width = 1024; c.height = 512;
    const g = c.getContext("2d");
    const bg = g.createLinearGradient(0, 0, 0, 512);
    bg.addColorStop(0, "#221710"); bg.addColorStop(0.55, "#0e0a07"); bg.addColorStop(1, "#060403");
    g.fillStyle = bg; g.fillRect(0, 0, 1024, 512);
    const glow = (x, y, r, col, a) => {
      const gr = g.createRadialGradient(x, y, 0, x, y, r);
      gr.addColorStop(0, col); gr.addColorStop(1, "rgba(0,0,0,0)");
      g.globalAlpha = a; g.fillStyle = gr; g.fillRect(x - r, y - r, r * 2, r * 2); g.globalAlpha = 1;
    };
    glow(250, 160, 150, "#ffdcb0", 1); glow(760, 190, 190, "#ff9c50", 0.85);
    glow(520, 480, 320, "#a0521f", 0.45); glow(930, 140, 90, "#bfe3d8", 0.55);
    g.fillStyle = "rgba(255,236,210,.95)"; g.fillRect(190, 140, 130, 22); g.fillRect(700, 176, 120, 16);
    const t = new THREE.CanvasTexture(c);
    t.mapping = THREE.EquirectangularReflectionMapping; t.colorSpace = THREE.SRGBColorSpace;
    return t;
  })();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromEquirectangular(envTex).texture;

  /* Hand-hammered copper: a dimpled bump map. */
  const hammer = (() => {
    const c = document.createElement("canvas"); c.width = c.height = 256;
    const g = c.getContext("2d"); g.fillStyle = "#808080"; g.fillRect(0, 0, 256, 256);
    for (let i = 0; i < 520; i++) {
      const x = Math.random() * 256, y = Math.random() * 256, r = 5 + Math.random() * 9;
      const gr = g.createRadialGradient(x, y, 0, x, y, r);
      gr.addColorStop(0, "rgba(40,40,40,.55)"); gr.addColorStop(0.7, "rgba(128,128,128,.1)"); gr.addColorStop(1, "rgba(160,160,160,0)");
      g.fillStyle = gr; g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
    }
    const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(5, 3);
    return t;
  })();
  const copperMat = (tone = 0xb8703f, rough = 0.3) => new THREE.MeshPhysicalMaterial({
    color: tone, metalness: 1, roughness: rough, bumpMap: hammer, bumpScale: 1.2,
    clearcoat: 0.35, clearcoatRoughness: 0.4, emissive: 0xff5a1a, emissiveIntensity: 0,
  });
  const V2 = (x, y) => new THREE.Vector2(x, y);
  const V3 = (x, y, z = 0) => new THREE.Vector3(x, y, z);

  const root = new THREE.Group(); scene.add(root);
  const still = new THREE.Group(); root.add(still);
  still.position.set(-1.05, -1.35, 0);
  const parts = {};
  const part = (key, base) => { const g = new THREE.Group(); g.userData = { base, mats: [] }; still.add(g); parts[key] = g; return g; };
  const add = (g, mesh) => { g.add(mesh); if (mesh.material && mesh.material.emissive) g.userData.mats.push(mesh.material); return mesh; };

  /* Furnace: coursed brick bound in riveted iron, an iron top plate, and an arched door.
     Behind the door sit the ceramic elements that replaced wood and gas ("from fire to
     element"). Everything that glows here scales with state.heat, so the still is cold
     on arrival and lights as the reader scrolls past the cue. */
  const furnace = part("furnace", V3(0, 0, 0));
  const glowTex = (() => {
    const c = document.createElement("canvas"); c.width = c.height = 128; const g = c.getContext("2d");
    const gr = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    gr.addColorStop(0, "rgba(255,170,80,.95)"); gr.addColorStop(0.4, "rgba(255,100,30,.5)"); gr.addColorStop(1, "rgba(255,70,10,0)");
    g.fillStyle = gr; g.fillRect(0, 0, 128, 128); return new THREE.CanvasTexture(c);
  })();
  const [brickTex, brickBump] = (() => {
    const c = document.createElement("canvas"), b = document.createElement("canvas");
    c.width = b.width = 512; c.height = b.height = 256;
    const g = c.getContext("2d"), h = b.getContext("2d");
    g.fillStyle = "#140c08"; g.fillRect(0, 0, 512, 256);
    h.fillStyle = "#303030"; h.fillRect(0, 0, 512, 256);
    const rows = 8, bh = 256 / rows, bw = 512 / 12;
    for (let r = 0; r < rows; r++) for (let i = -1; i < 13; i++) {
      const x = i * bw + (r % 2 ? bw / 2 : 0), y = r * bh;
      g.fillStyle = `hsl(${10 + Math.random() * 12} ${38 + Math.random() * 18}% ${20 + Math.random() * 14}%)`;
      g.fillRect(x + 2.5, y + 2.5, bw - 5, bh - 5);
      g.fillStyle = `rgba(0,0,0,${(Math.random() * 0.4).toFixed(2)})`; g.fillRect(x + 2.5, y + 2.5, bw - 5, bh - 5);
      h.fillStyle = `rgb(${190 + (Math.random() * 40) | 0},${190},${190})`; h.fillRect(x + 2.5, y + 2.5, bw - 5, bh - 5);
    }
    const soot = g.createLinearGradient(0, 0, 0, 256); soot.addColorStop(0, "rgba(0,0,0,.45)"); soot.addColorStop(0.35, "rgba(0,0,0,0)");
    g.fillStyle = soot; g.fillRect(0, 0, 512, 256);
    return [c, b].map((cv, k) => { const t = new THREE.CanvasTexture(cv); if (!k) t.colorSpace = THREE.SRGBColorSpace; t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(3, 1); return t; });
  })();
  const brickMat = new THREE.MeshStandardMaterial({ map: brickTex, bumpMap: brickBump, bumpScale: 2.2, roughness: 0.92, metalness: 0, emissive: 0xff5a1a, emissiveIntensity: 0 });
  add(furnace, new THREE.Mesh(new THREE.CylinderGeometry(0.98, 1.05, 0.7, 64, 1, true), brickMat)).position.y = 0.35;
  const ironMat = new THREE.MeshStandardMaterial({ color: 0x24201d, metalness: 0.85, roughness: 0.5 });
  const plate = new THREE.Mesh(new THREE.CylinderGeometry(1.04, 1.02, 0.06, 64), ironMat); plate.position.y = 0.72; furnace.add(plate);
  const rivetGeo = new THREE.SphereGeometry(0.016, 8, 6);
  [[0.1, 1.045], [0.6, 0.995]].forEach(([y, r]) => {
    const band = new THREE.Mesh(new THREE.TorusGeometry(r, 0.022, 8, 72), ironMat); band.rotation.x = Math.PI / 2; band.position.y = y; furnace.add(band);
    const rivets = new THREE.InstancedMesh(rivetGeo, ironMat, 30), m = new THREE.Matrix4();
    for (let i = 0; i < 30; i++) { const a = (i / 30) * Math.PI * 2; m.setPosition(Math.sin(a) * (r + 0.022), y, Math.cos(a) * (r + 0.022)); rivets.setMatrixAt(i, m); }
    furnace.add(rivets);
  });
  // The arched door: an iron frame round a recess of ceramic elements (cold grey, then glowing).
  const arch = (w, h, r) => { const s = new THREE.Shape(); s.moveTo(-w, 0); s.lineTo(-w, h - r); s.absarc(0, h - r, w, Math.PI, 0, true); s.lineTo(w, 0); s.lineTo(-w, 0); return s; };
  const frameShape = arch(0.25, 0.5, 0.25); frameShape.holes.push(arch(0.19, 0.44, 0.19));
  const doorFrame = new THREE.Mesh(new THREE.ExtrudeGeometry(frameShape, { depth: 0.05, bevelEnabled: true, bevelThickness: 0.012, bevelSize: 0.01, bevelSegments: 2, curveSegments: 24 }), ironMat);
  doorFrame.position.set(0, 0.06, 1.0); furnace.add(doorFrame);
  // Cold: logs laid in the grate. Lit: a noise-driven fire over a bed of embers, taller with heat.
  const logsTex = (() => {
    const c = document.createElement("canvas"); c.width = 128; c.height = 160; const g = c.getContext("2d");
    const bg = g.createLinearGradient(0, 0, 0, 160); bg.addColorStop(0, "#030202"); bg.addColorStop(1, "#0d0806");
    g.fillStyle = bg; g.fillRect(0, 0, 128, 160);
    g.fillStyle = "#1c1511"; g.fillRect(8, 146, 112, 6);
    const log = (x, y, w, h, rot) => {
      g.save(); g.translate(x, y); g.rotate(rot);
      const lg = g.createLinearGradient(0, -h / 2, 0, h / 2); lg.addColorStop(0, "#3b2a1f"); lg.addColorStop(0.5, "#241910"); lg.addColorStop(1, "#120c08");
      g.fillStyle = lg; g.beginPath(); g.roundRect(-w / 2, -h / 2, w, h, h / 2); g.fill();
      g.fillStyle = "#4a3627"; g.beginPath(); g.ellipse(w / 2 - 3, 0, 4, h / 2 - 1, 0, 0, Math.PI * 2); g.fill();
      g.restore();
    };
    log(46, 138, 70, 14, 0.12); log(84, 136, 68, 14, -0.16); log(64, 124, 60, 12, 0.02);
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
  })();
  const recessGeo = new THREE.ShapeGeometry(arch(0.19, 0.44, 0.19), 24);
  recessGeo.computeBoundingBox();
  { const bb = recessGeo.boundingBox, uv = recessGeo.attributes.uv, p = recessGeo.attributes.position;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, (p.getX(i) - bb.min.x) / (bb.max.x - bb.min.x), (p.getY(i) - bb.min.y) / (bb.max.y - bb.min.y)); }
  const recessCold = new THREE.Mesh(recessGeo, new THREE.MeshBasicMaterial({ map: logsTex }));
  const fireMat = new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    uniforms: { uTime: { value: 0 }, uHeat: { value: 0 } },
    vertexShader: "varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }",
    fragmentShader: `uniform float uTime; uniform float uHeat; varying vec2 vUv;
      float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float noise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
        return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y); }
      float fbm(vec2 p){ float v = 0.0, a = 0.5; for (int i = 0; i < 5; i++){ v += a * noise(p); p *= 2.03; a *= 0.5; } return v; }
      void main(){
        vec2 uv = vUv;
        float n = fbm(vec2(uv.x * 3.2 + sin(uTime * 0.7) * 0.2, uv.y * 2.4 - uTime * 1.9));
        float reach = 0.25 + 0.75 * uHeat;
        float body = (reach - uv.y * 1.15) + n * 0.85 - 0.55;
        float side = 1.0 - pow(abs(uv.x - 0.5) * 2.0, 2.2);
        float f = clamp(body * 2.2 * side, 0.0, 1.0);
        vec3 col = mix(vec3(0.55, 0.06, 0.0), vec3(1.0, 0.42, 0.06), smoothstep(0.05, 0.45, f));
        col = mix(col, vec3(1.0, 0.86, 0.55), smoothstep(0.55, 0.95, f));
        float bed = smoothstep(0.24, 0.05, uv.y) * (0.45 + 0.55 * noise(vec2(uv.x * 16.0, uTime * 0.9))) * side;
        col += vec3(1.0, 0.32, 0.05) * bed * 0.9;
        float a = clamp(f + bed * 0.8, 0.0, 1.0) * smoothstep(0.0, 0.2, uHeat);
        gl_FragColor = vec4(col * a, a);
      }`,
  });
  const recessHot = new THREE.Mesh(recessGeo, fireMat);
  recessCold.position.set(0, 0.06, 1.03); recessHot.position.set(0, 0.06, 1.032); furnace.add(recessCold, recessHot);
  const doorGlow = new THREE.Sprite(new THREE.SpriteMaterial({ map: glowTex, blending: THREE.AdditiveBlending, transparent: true, opacity: 0, depthWrite: false }));
  doorGlow.scale.set(1.05, 0.95, 1); doorGlow.position.set(0, 0.3, 1.12); furnace.add(doorGlow);
  // Heat escaping between the plate and the pot, and a warm pool on the floor below the door.
  const slitMat = new THREE.MeshBasicMaterial({ color: 0xff8a3a, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false });
  const slit = new THREE.Mesh(new THREE.TorusGeometry(0.745, 0.016, 8, 72), slitMat); slit.rotation.x = Math.PI / 2; slit.position.y = 0.755; furnace.add(slit);
  const floorMat = new THREE.MeshBasicMaterial({ map: glowTex, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false });
  const floorGlow = new THREE.Mesh(new THREE.PlaneGeometry(2.2, 1.3), floorMat); floorGlow.rotation.x = -Math.PI / 2; floorGlow.position.set(0, 0.004, 1.35); furnace.add(floorGlow);
  const fireLight = new THREE.PointLight(0xff7a2a, 0, 3.2, 1.8); fireLight.position.set(0, 0.32, 1.35); furnace.add(fireLight);
  const slitLight = new THREE.PointLight(0xff6a20, 0, 2.4, 1.6); slitLight.position.set(0, 0.85, 0.95); furnace.add(slitLight);

  // Pot
  const pot = part("pot", V3(0, 0, 0));
  const potProfile = [V2(0.001, 0), V2(0.5, 0.02), V2(0.74, 0.14), V2(0.84, 0.38), V2(0.8, 0.62), V2(0.62, 0.84), V2(0.34, 0.98), V2(0.2, 1.02), V2(0.2, 1.16)];
  add(pot, new THREE.Mesh(new THREE.LatheGeometry(potProfile, 72), copperMat())).position.y = 0.62;
  const ringMat = copperMat(0x8a4a24, 0.45);
  const ring = add(pot, new THREE.Mesh(new THREE.TorusGeometry(0.83, 0.025, 12, 72), ringMat)); ring.rotation.x = Math.PI / 2; ring.position.y = 0.62 + 0.4;

  // Helmet
  const helmet = part("helmet", V3(0, 0, 0));
  const helmProfile = [V2(0.2, 0), V2(0.3, 0.03), V2(0.52, 0.18), V2(0.58, 0.36), V2(0.5, 0.54), V2(0.32, 0.68), V2(0.14, 0.76), V2(0.09, 0.8)];
  add(helmet, new THREE.Mesh(new THREE.LatheGeometry(helmProfile, 72), copperMat(0xc07a45, 0.26))).position.y = 1.78;
  const hr = add(helmet, new THREE.Mesh(new THREE.TorusGeometry(0.21, 0.03, 12, 48), ringMat)); hr.rotation.x = Math.PI / 2; hr.position.y = 1.79;

  // Swan neck + a light pulse travelling through it (the vapour)
  const neck = part("neck", V3(0, 0, 0));
  const neckCurve = new THREE.CatmullRomCurve3([V3(0, 2.52), V3(0.3, 2.72), V3(0.9, 2.74), V3(1.5, 2.38), V3(2.05, 1.9)]);
  add(neck, new THREE.Mesh(new THREE.TubeGeometry(neckCurve, 96, 0.075, 18), copperMat()));
  const pulseMat = new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    uniforms: { uTime: { value: 0 }, uAmp: { value: 1 } },
    vertexShader: "varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }",
    fragmentShader: "uniform float uTime; uniform float uAmp; varying vec2 vUv; void main(){ float b = fract(vUv.x * 2.5 - uTime * 0.45); float a = smoothstep(0.0, 0.18, b) * smoothstep(0.55, 0.18, b); gl_FragColor = vec4(vec3(1.0, 0.62, 0.3) * a * uAmp, a * uAmp * 0.85); }",
  });
  neck.add(new THREE.Mesh(new THREE.TubeGeometry(neckCurve, 96, 0.09, 18), pulseMat));

  // Condenser tub, with the worm visible when the tub turns to glass
  const cond = part("condenser", V3(0, 0, 0));
  const tubMat = copperMat(0x9a5a30, 0.42); tubMat.transparent = true;
  const tub = add(cond, new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 1.5, 56, 1, true), tubMat)); tub.position.set(2.3, 1.1, 0);
  tubMat.side = THREE.DoubleSide;
  [0.36, 1.84].forEach((y) => { const t = add(cond, new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.03, 12, 56), ringMat)); t.rotation.x = Math.PI / 2; t.position.set(2.3, y, 0); });
  const coilPts = [];
  for (let i = 0; i <= 240; i++) { const t = i / 240, a = t * Math.PI * 2 * 5.5; coilPts.push(V3(2.3 + Math.cos(a) * 0.26, 1.78 - t * 1.28, Math.sin(a) * 0.26)); }
  add(cond, new THREE.Mesh(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(coilPts), 480, 0.035, 10), copperMat(0xd08a52, 0.25)));
  const spout = add(cond, new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.32, 12), copperMat()));
  spout.rotation.z = Math.PI / 2 - 0.3; spout.position.set(2.86, 0.48, 0);

  /* Receiver: a lab beaker of new spirit. Thin flared glass, a thick base, white graduations,
     water-white spirit with a bright surface. Each drop swells at the spout tip, lets go,
     falls under gravity and rings out across the surface. */
  const recv = part("receiver", V3(0, 0, 0));
  const BX = 3.05, TIP = V3(3.013, 0.405), LEVEL = 0.2;
  const glassMat = new THREE.MeshPhysicalMaterial({ color: 0xeef6f2, metalness: 0, roughness: 0.04, clearcoat: 1, clearcoatRoughness: 0.05, envMapIntensity: 2.2,
    transparent: true, opacity: 0.2, side: THREE.DoubleSide, depthWrite: false, emissive: 0xff5a1a, emissiveIntensity: 0 });
  const beakerProfile = [V2(0.001, 0), V2(0.16, 0), V2(0.17, 0.012), V2(0.17, 0.31), V2(0.172, 0.33), V2(0.182, 0.345)];
  const beaker = add(recv, new THREE.Mesh(new THREE.LatheGeometry(beakerProfile, 56), glassMat)); beaker.position.set(BX, 0, 0); beaker.renderOrder = 3;
  const lipMat = glassMat.clone(); lipMat.opacity = 0.55;
  const lip = new THREE.Mesh(new THREE.TorusGeometry(0.182, 0.0055, 8, 56), lipMat); lip.rotation.x = Math.PI / 2; lip.position.set(BX, 0.345, 0); lip.renderOrder = 3; recv.add(lip);
  const baseMat = glassMat.clone(); baseMat.opacity = 0.16; baseMat.side = THREE.FrontSide;
  const baseGlass = new THREE.Mesh(new THREE.CylinderGeometry(0.162, 0.162, 0.022, 48), baseMat); baseGlass.position.set(BX, 0.012, 0); recv.add(baseGlass);
  const markMat = new THREE.MeshBasicMaterial({ color: 0xf2ece0, transparent: true, opacity: 0.7 });
  for (let i = 0; i < 5; i++) {
    const long = i % 2 === 0, w = long ? 0.07 : 0.04, y = 0.07 + i * 0.05;
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, 0.0035, 0.002), markMat);
    m.position.set(BX - 0.05 + w / 2, y, 0.171); recv.add(m);
  }
  const spiritMat = new THREE.MeshPhysicalMaterial({ color: 0xf4f8f6, metalness: 0, roughness: 0.02, transparent: true, opacity: 0.1, depthWrite: false, emissive: 0xffd9a0, emissiveIntensity: 0.02 });
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.163, 0.16, LEVEL - 0.024, 48, 1, true), spiritMat); body.position.set(BX, 0.024 + (LEVEL - 0.024) / 2, 0); body.renderOrder = 2; recv.add(body);
  const surfMat = new THREE.MeshPhysicalMaterial({ color: 0xffffff, metalness: 0, roughness: 0, clearcoat: 1, envMapIntensity: 3, transparent: true, opacity: 0.2, depthWrite: false, emissive: 0xffe0b0, emissiveIntensity: 0.03 });
  const surface = new THREE.Mesh(new THREE.CircleGeometry(0.163, 48), surfMat); surface.rotation.x = -Math.PI / 2; surface.position.set(BX, LEVEL, 0); surface.renderOrder = 2; recv.add(surface);
  const menMat = new THREE.MeshBasicMaterial({ color: 0xfff4e0, transparent: true, opacity: 0.5, depthWrite: false });
  const meniscus = new THREE.Mesh(new THREE.TorusGeometry(0.162, 0.0028, 6, 56), menMat); meniscus.rotation.x = Math.PI / 2; meniscus.position.set(BX, LEVEL + 0.002, 0); meniscus.renderOrder = 2; recv.add(meniscus);
  const ripples = [0, 1, 2].map(() => {
    const r = new THREE.Mesh(new THREE.RingGeometry(0.9, 1, 40), new THREE.MeshBasicMaterial({ color: 0xfff1d8, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }));
    r.rotation.x = -Math.PI / 2; r.position.set(TIP.x, LEVEL + 0.002, 0); r.userData.born = -9; recv.add(r); return r;
  });
  const dropGeo = new THREE.LatheGeometry([V2(0.001, -0.02), V2(0.011, -0.016), V2(0.015, -0.006), V2(0.012, 0.006), V2(0.005, 0.018), V2(0.001, 0.024)], 20);
  const dropMat = new THREE.MeshPhysicalMaterial({ color: 0xffffff, metalness: 0, roughness: 0, clearcoat: 1, envMapIntensity: 3, transparent: true, opacity: 0.75, emissive: 0xffe2b8, emissiveIntensity: 0.3 });
  const drop = new THREE.Mesh(dropGeo, dropMat); drop.position.copy(TIP); recv.add(drop);
  const DRIP = { period: 1.15, form: 0.62, g: 7 };
  let lastCycle = -1, rippleNext = 0;

  // Ground shadow
  const shadowTex = (() => { const c = document.createElement("canvas"); c.width = c.height = 128; const g = c.getContext("2d"); const gr = g.createRadialGradient(64, 64, 0, 64, 64, 64); gr.addColorStop(0, "rgba(0,0,0,.7)"); gr.addColorStop(1, "rgba(0,0,0,0)"); g.fillStyle = gr; g.fillRect(0, 0, 128, 128); return new THREE.CanvasTexture(c); })();
  const shadow = new THREE.Mesh(new THREE.PlaneGeometry(6, 2.4), new THREE.MeshBasicMaterial({ map: shadowTex, transparent: true, depthWrite: false }));
  shadow.rotation.x = -Math.PI / 2; shadow.position.set(1.2, 0.001, 0); still.add(shadow);

  // Lights: warm key, cool verdigris rim
  const key = new THREE.DirectionalLight(0xffe2c0, 1.4); key.position.set(-3, 5, 5); scene.add(key);
  const rim = new THREE.DirectionalLight(0x86bcab, 1.1); rim.position.set(4, 2.5, -4); scene.add(rim);
  scene.add(new THREE.AmbientLight(0x3a2618, 0.6));

  // Exploded offsets and label anchors
  const OFF = { furnace: V3(0, -0.5, 0.1), pot: V3(0, 0, 0), helmet: V3(-0.05, 0.8, 0), neck: V3(0.2, 1.2, 0), condenser: V3(0.9, 0.15, 0), receiver: V3(1.45, -0.05, 0.1) };
  const ANCHOR = { furnace: V3(0.95, 0.35, 0.3), pot: V3(0.86, 1.15, 0.2), helmet: V3(0.55, 2.2, 0.1), neck: V3(1.0, 2.82, 0), condenser: V3(2.78, 1.5, 0), receiver: V3(3.3, 0.3, 0) };
  const labelMap = {}; labels.forEach((l) => { labelMap[l.dataset.label] = l; });

  /* Layout: fit the still to the stage, sitting right on wide screens. */
  let W = 1, H = 1, wide = true;
  const layout = () => {
    W = stage.clientWidth; H = stage.clientHeight;
    renderer.setSize(W, H, false);
    camera.aspect = W / H;
    wide = W >= 990;
    const needW = wide ? 12.5 : 5.6;
    const tan = Math.tan((camera.fov * Math.PI) / 360);
    const distForW = needW / (2 * tan * camera.aspect);
    const distForH = 4.4 / (2 * tan);
    camera.userData.dist = Math.max(distForW, distForH, 8);
    camera.updateProjectionMatrix();
  };
  new ResizeObserver(layout).observe(stage); layout();

  /* Drag to turn (only over empty stage; text columns keep their own events). */
  let userRot = 0, userVel = 0, dragging = false, lastX = 0;
  canvas.addEventListener("pointerdown", (e) => { dragging = true; lastX = e.clientX; canvas.setPointerCapture(e.pointerId); });
  canvas.addEventListener("pointermove", (e) => { if (!dragging) return; const dx = e.clientX - lastX; lastX = e.clientX; userVel = dx * 0.006; userRot += userVel; });
  const end = () => { dragging = false; };
  canvas.addEventListener("pointerup", end); canvas.addEventListener("pointercancel", end);

  /* Render only while the section is on screen and the tab is visible. */
  let visible = true, raf = 0, first = true;
  new IntersectionObserver((ents) => { visible = ents[0].isIntersecting; if (visible && !raf) raf = requestAnimationFrame(frame); }, { rootMargin: "100px" }).observe(sec);
  document.addEventListener("visibilitychange", () => { if (!document.hidden && visible && !raf) raf = requestAnimationFrame(frame); });

  const tmp = new THREE.Vector3();
  const clock = new THREE.Clock();
  const MAP = { all: null, still: ["neck", "condenser", "receiver"], pot: ["pot"] };
  function frame() {
    raf = 0;
    if (!visible || document.hidden) return;
    const t = clock.getElapsedTime();
    state.explode = lerp(state.explode, state.explodeTarget, 0.075);
    const ex = state.explode;

    // Rotation: a slow sway, turned by scroll, settling to a readable 3/4 view when exploded.
    if (!dragging) { userVel *= 0.94; userRot += userVel; }
    const heroRot = -0.55 + Math.sin(t * 0.25) * 0.12 + state.heroP * 1.6;
    root.rotation.y = lerp(heroRot, 0.28 + Math.sin(t * 0.2) * 0.04, ex) + userRot;
    root.rotation.x = lerp(0.06, 0.1, ex);

    // Camera: frame right on wide screens; pull back a touch when exploded.
    const dist = camera.userData.dist * lerp(1, 1.18, ex);
    const shiftX = wide ? lerp(-2.7, -2.5, ex) : 0;
    camera.position.set(shiftX, lerp(0.35, 0.55, ex) + (wide ? 0 : -0.6), dist);
    camera.lookAt(shiftX, lerp(0.1, 0.35, ex) + (wide ? 0 : -0.6), 0);

    // Parts: explode, glow the focused one
    const keys = MAP[state.focus] !== undefined ? MAP[state.focus] : [state.focus];
    for (const k in parts) {
      const g = parts[k];
      g.position.copy(g.userData.base).addScaledVector(OFF[k], ex);
      const on = !!keys && keys.includes(k);
      const glow = on ? 0.18 + Math.sin(t * 3) * 0.06 : 0;
      g.userData.mats.forEach((m) => { m.emissiveIntensity = lerp(m.emissiveIntensity, glow, 0.1); });
    }
    tubMat.opacity = lerp(1, 0.16, ex);
    tubMat.depthWrite = ex < 0.5;

    // Heat: the fire catches as the reader scrolls past the cue, then the vapour runs and the spirit drips.
    state.heat = lerp(state.heat, state.heatTarget, 0.035);
    const h = state.heat, flick = 1 + Math.sin(t * 13) * 0.12 + Math.sin(t * 7.3) * 0.16 + Math.sin(t * 23.1) * 0.06;
    fireMat.uniforms.uTime.value = t; fireMat.uniforms.uHeat.value = h;
    fireLight.intensity = 6 * h * flick;
    slitLight.intensity = 3.2 * h * (0.9 + (flick - 1) * 0.6);
    doorGlow.material.opacity = 0.75 * h * flick;
    slitMat.opacity = 0.85 * h * (0.85 + (flick - 1) * 0.5);
    floorMat.opacity = 0.45 * h * flick;
    renderer.toneMappingExposure = 1.02 + 0.14 * h;
    scene.environmentIntensity = 0.85 + 0.2 * h;
    spiritMat.emissiveIntensity = 0.01 + 0.04 * h; surfMat.emissiveIntensity = 0.02 + 0.06 * h; menMat.opacity = 0.35 + 0.3 * h;
    const run = clamp((h - 0.55) / 0.45, 0, 1);
    pulseMat.uniforms.uTime.value = t; pulseMat.uniforms.uAmp.value = run;
    // A drop swells at the tip, lets go, falls under gravity and rings out on the surface.
    const cyc = Math.floor(t / DRIP.period), ph = (t % DRIP.period);
    if (run < 0.6) drop.scale.setScalar(0.001);
    else if (ph < DRIP.form) {
      const k = ph / DRIP.form, sw = k * k;
      drop.position.set(TIP.x, TIP.y - sw * 0.012, 0);
      drop.scale.set(0.35 + 0.65 * sw, 0.3 + 0.8 * sw, 0.35 + 0.65 * sw);
    } else {
      const ft = ph - DRIP.form, y = TIP.y - 0.012 - 0.5 * DRIP.g * ft * ft;
      if (y > LEVEL + 0.01) { drop.position.set(TIP.x, y, 0); drop.scale.set(0.9, 1.25, 0.9); }
      else {
        drop.scale.setScalar(0.001);
        if (cyc !== lastCycle) { lastCycle = cyc; ripples[rippleNext].userData.born = t; rippleNext = (rippleNext + 1) % ripples.length; }
      }
    }
    ripples.forEach((r) => {
      const age = t - r.userData.born, k = clamp(age / 0.9, 0, 1);
      r.scale.setScalar(0.012 + k * 0.1);
      r.material.opacity = age < 0.9 ? (1 - k) * 0.8 : 0;
    });

    // Labels follow their parts on screen
    still.updateMatrixWorld();
    for (const k in labelMap) {
      if (!parts[k]) continue;
      tmp.copy(ANCHOR[k]).add(parts[k].position);
      still.localToWorld(tmp); tmp.project(camera);
      const x = (tmp.x * 0.5 + 0.5) * W, y = (-tmp.y * 0.5 + 0.5) * H;
      labelMap[k].style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) translate(-5px, -50%)`;
    }

    renderer.render(scene, camera);
    if (first) { first = false; sec.classList.add("is-gl"); }
    raf = requestAnimationFrame(frame);
  }
  raf = requestAnimationFrame(frame);
}

/* ==========================================================================
   PROCESS — fly the SVG camera from station to station
   ========================================================================== */
function initProcess() {
  const sec = $("[data-psb-process]");
  if (!sec) return;
  const svg = $("[data-psb-machine]", sec);
  const steps = $$("[data-psb-step]", sec);
  const rails = $$("[data-psb-rail]", sec);
  const out = (k) => $(`[data-psb-out="${k}"]`, sec);
  const VIEWS = {
    s0: [0, 200, 2400, 700], s1: [0, 180, 780, 720], s2: [340, 320, 560, 560], s3: [780, 300, 940, 600],
    s4: [830, 300, 620, 480], s5: [1420, 600, 360, 300], s6: [1720, 520, 420, 380], s7: [2120, 500, 300, 400],
  };
  const cam = { x: 0, y: 200, w: 2400, h: 700 };
  const applyCam = () => svg.setAttribute("viewBox", `${cam.x.toFixed(1)} ${cam.y.toFixed(1)} ${cam.w.toFixed(1)} ${cam.h.toFixed(1)}`);
  const abv = { v: 0 };
  let current = -2;

  const go = (i) => {
    if (i === current) return;
    current = i;
    const step = steps[i];
    const scene = step ? step.dataset.scene : "s0";
    const v = VIEWS[scene] || VIEWS.s0;
    svg.setAttribute("data-scene", scene);
    steps.forEach((s, j) => s.classList.toggle("is-on", j === i));
    rails.forEach((r, j) => { r.classList.toggle("is-on", j === i); r.classList.toggle("is-done", j < i); });
    if (gsap && !REDUCED) gsap.to(cam, { x: v[0], y: v[1], w: v[2], h: v[3], duration: 1.5, ease: "power3.inOut", onUpdate: applyCam, overwrite: true });
    else { Object.assign(cam, { x: v[0], y: v[1], w: v[2], h: v[3] }); applyCam(); }
    const target = step ? parseFloat(step.dataset.abv) || 0 : 0;
    const abvEl = out("abv");
    if (gsap && !REDUCED) gsap.to(abv, { v: target, duration: 1.2, ease: "power2.out", overwrite: true, onUpdate: () => { abvEl.textContent = Math.round(abv.v); } });
    else abvEl.textContent = Math.round(target);
    out("stage").textContent = step ? step.dataset.stage || "—" : "—";
    out("temp").textContent = step ? step.dataset.temp || "—" : "—";
    out("bar").style.transform = `scaleX(${clamp(target / 80, 0, 1).toFixed(3)})`;
  };

  // The step whose box crosses the middle of the screen is the current one.
  const pick = () => {
    const mid = innerHeight * 0.5;
    let idx = -1;
    steps.forEach((s, j) => { const r = s.getBoundingClientRect(); if (r.top <= mid) idx = j; });
    const first = steps[0] && steps[0].getBoundingClientRect().top > mid;
    go(first ? -1 : idx);
  };
  let ticking = false;
  addEventListener("scroll", () => { if (!ticking) { ticking = true; requestAnimationFrame(() => { ticking = false; pick(); }); } }, { passive: true });
  addEventListener("resize", pick);
  pick();
}

/* ==========================================================================
   MAKE THE CUT — the spirit-safe game
   ========================================================================== */
function initCut() {
  const sec = $("[data-psb-cut]");
  if (!sec) return;
  const bench = $("[data-cut-bench]", sec);
  const [A0, A1, A2, A3] = sec.dataset.abv.split(",").map(Number);
  const HE = clamp(+sec.dataset.headsEnd / 100, 0.01, 0.5);
  const TE = clamp(+sec.dataset.heartEnd / 100, HE + 0.05, 0.95);
  const DUR = clamp(+sec.dataset.duration || 20, 5, 60) * 1000;
  // Strength falls fast through the heads, steadily through the heart, then collapses in the tails.
  const abvAt = (t) => (t < HE ? lerp(A0, A1, t / HE)
    : t < TE ? lerp(A1, A2, Math.pow((t - HE) / (TE - HE), 1.25))
      : lerp(A2, A3, 1 - Math.pow(1 - (t - TE) / (1 - TE), 1.6)));
  const X = (t) => 40 + t * 550;
  const Y = (a) => 280 - (a / 90) * 266;
  const pathTo = (t1) => { let d = ""; const n = Math.max(2, Math.round(t1 * 160)); for (let i = 0; i <= n; i++) { const t = (i / n) * t1; d += (i ? "L" : "M") + X(t).toFixed(1) + " " + Y(abvAt(t)).toFixed(1); } return d; };

  const el = (k) => $(`[data-cut-${k}]`, bench);
  const btn = el("btn"), curve = el("curve"), ghost = el("ghost"), dot = el("dot"), pick = el("pick");
  const abvOut = el("abv"), noseOut = el("nose"), status = el("status"), result = el("result"), verdict = el("verdict");
  const line1 = $('[data-cut-line="1"]', bench), line2 = $('[data-cut-line="2"]', bench);
  const copy = el("copy").content;
  const txt = (sel) => (copy.querySelector(sel) || {}).textContent || "";
  const jars = { heads: $('[data-jar="heads"]', bench), heart: $('[data-jar="heart"]', bench), tails: $('[data-jar="tails"]', bench) };
  const zones = { heads: [0, HE], heart: [HE, TE], tails: [TE, 1] };
  Object.entries(zones).forEach(([k, [a, b]]) => { const r = $(`[data-zone="${k}"]`, bench); r.setAttribute("x", X(a)); r.setAttribute("width", X(b) - X(a)); });
  ghost.setAttribute("d", pathTo(1));
  const zoneOf = (t) => (t < HE ? "heads" : t < TE ? "heart" : "tails");

  let phase = "idle", t0 = 0, c1 = null, c2 = null, raf = 0, lastZone = "";
  const fill = { heads: 0, heart: 0, tails: 0 };
  const label = (k) => btn.getAttribute(`data-l-${k}`);
  const say = (m) => { status.textContent = m; };
  const reset = () => {
    phase = "idle"; c1 = c2 = null; lastZone = ""; Object.keys(fill).forEach((k) => { fill[k] = 0; jars[k].style.setProperty("--f", "0%"); });
    curve.setAttribute("d", ""); dot.setAttribute("cx", X(0)); dot.setAttribute("cy", Y(A0));
    pick.setAttribute("width", 0); line1.style.display = "none"; line2.style.display = "none";
    abvOut.textContent = "—"; noseOut.textContent = "—"; result.hidden = true; bench.classList.remove("is-done");
  };
  const overlap = (a, b, c, d) => Math.max(0, Math.min(b, d) - Math.max(a, c));
  const stats = $(".psb-bench__stats", bench);
  const finish = () => {
    phase = "done"; cancelAnimationFrame(raf);
    btn.disabled = false; btn.textContent = label("again");
    bench.classList.add("is-done"); result.hidden = false;
    // No cut at all is not a result: there is no heart to judge, so say so and invite a retry.
    if (c1 == null) {
      verdict.textContent = txt('[data-v="none"]');
      stats.hidden = true;
      say(verdict.textContent);
      return;
    }
    stats.hidden = false;
    const a = c1, b = c2 == null ? 1 : Math.max(c2, a);
    const span = b - a;
    const heartKept = overlap(a, b, HE, TE) / (TE - HE);
    const headsIn = span > 0 ? overlap(a, b, 0, HE) / span : 0;
    const tailsIn = span > 0 ? overlap(a, b, TE, 1) / span : 0;
    let sum = 0, n = 0; for (let t = a; t <= b; t += 0.002) { sum += abvAt(t); n++; }
    const avg = n ? sum / n : 0;
    const v = headsIn > 0.06 ? "early" : tailsIn > 0.06 ? "late" : heartKept < 0.75 ? "shy" : "good";
    verdict.textContent = txt(`[data-v="${v}"]`);
    $('[data-cut-stat="heart"]', bench).textContent = `${Math.round(heartKept * 100)}%`;
    $('[data-cut-stat="heads"]', bench).textContent = `${Math.round(headsIn * 100)}%`;
    $('[data-cut-stat="tails"]', bench).textContent = `${Math.round(tailsIn * 100)}%`;
    $('[data-cut-stat="abv"]', bench).textContent = n ? `${avg.toFixed(1)}%` : "—";
    say(`${verdict.textContent} ${Math.round(heartKept * 100)}% of the heart kept.`);
  };
  const tick = (now) => {
    const t = clamp((now - t0) / DUR, 0, 1);
    const a = abvAt(t), z = zoneOf(t);
    curve.setAttribute("d", pathTo(t));
    dot.setAttribute("cx", X(t)); dot.setAttribute("cy", Y(a));
    abvOut.textContent = a.toFixed(1);
    if (z !== lastZone) { lastZone = z; noseOut.textContent = txt(`[data-n="${z}"]`); say(`${Math.round(a)}% alcohol. ${noseOut.textContent}.`); }
    // Which jar the stream is running into is the player's call, not the truth.
    const bucket = c1 == null ? "heads" : c2 == null ? "heart" : "tails";
    fill[bucket] += 1 / 60 / (DUR / 1000);
    const cap = { heads: 0.35, heart: 0.6, tails: 0.6 };
    Object.keys(fill).forEach((k) => jars[k].style.setProperty("--f", `${Math.min(100, (fill[k] / cap[k]) * 100)}%`));
    if (c1 != null) { pick.setAttribute("x", X(c1)); pick.setAttribute("width", Math.max(0, X(c2 == null ? t : c2) - X(c1))); }
    if (t >= 1) { finish(); return; }
    raf = requestAnimationFrame(tick);
  };
  const setLine = (ln, t) => { ln.setAttribute("x1", X(t)); ln.setAttribute("x2", X(t)); ln.style.display = ""; };
  btn.addEventListener("click", () => {
    const now = performance.now();
    const t = clamp((now - t0) / DUR, 0, 1);
    // "Run it again" starts straight away. A nested btn.click() is ignored by browsers while
    // this click is still being dispatched, which made a replay need two taps.
    if (phase === "idle" || phase === "done") { reset(); phase = "run"; t0 = now; btn.textContent = label("heart"); raf = requestAnimationFrame(tick); }
    else if (phase === "run" && c1 == null) { c1 = t; setLine(line1, t); btn.textContent = label("tails"); say(`Cut to the heart at ${Math.round(abvAt(t))}%.`); }
    else if (phase === "run" && c2 == null) { c2 = t; setLine(line2, t); btn.textContent = label("wait"); btn.disabled = true; say(`Cut to the tails at ${Math.round(abvAt(t))}%.`); }
  });
  reset();
}

/* ==========================================================================
   BARREL — age it with a slider
   ========================================================================== */
function initBarrel() {
  const sec = $("[data-psb-barrel]");
  if (!sec) return;
  const range = $("[data-cask-range]", sec);
  const spirit = $("[data-cask-spirit]", sec), men = $("[data-cask-meniscus]", sec), angels = $("[data-cask-angels]", sec);
  const out = (k) => $(`[data-cask-out="${k}"]`, sec);
  const bands = $$("[data-cask-band]", sec).map((li) => ({ li, from: +li.dataset.caskBand, colour: li.dataset.colour, notes: ($("span", li) || li).textContent.trim() })).sort((a, b) => a.from - b.from);
  const facts = $$("[data-cask-fact]", sec).map((li) => ({ li, year: +li.dataset.caskFact })).sort((a, b) => a.year - b.year);
  const ticks = $$("[data-cask-tick]", sec);
  const meters = $$("[data-cask-meter]", sec);
  const marker = $("[data-cask-marker]", sec);
  const unit = out("unit").textContent;
  let rate = (+sec.dataset.rateCool || 20) / 10;
  const colourAt = spiritAt;
  const update = () => {
    const y = +range.value;
    const left = 100 * Math.pow(1 - rate / 100, y);
    const top = 398 - 0.93 * (left / 100) * 276;
    spirit.setAttribute("y", top.toFixed(1));
    men.style.transform = `translateY(${(top - 130).toFixed(1)}px)`;
    sec.style.setProperty("--spirit", colourAt(y));
    spirit.style.fill = colourAt(y);
    sec.style.setProperty("--spirit-o", lerp(0.1, 0.96, clamp(y / 5, 0, 1)).toFixed(2));
    angels.style.setProperty("--angel", y > 0 ? (0.35 + rate / 8).toFixed(2) : 0);
    out("years").textContent = y;
    out("unit").textContent = unit;
    out("left").textContent = left.toFixed(left < 100 ? 1 : 0);
    const trend = out("trend");
    if (trend) {
      const hot = !!$('[data-cask-climate] input[value="hot"]:checked', sec);
      trend.textContent = y === 0 ? sec.dataset.trendNone : (hot ? sec.dataset.trendHot : sec.dataset.trendCool);
      trend.dataset.dir = y === 0 ? "" : (hot ? "up" : "down");
    }
    const band = bands.filter((b) => b.from <= y).pop();
    bands.forEach((b) => b.li.classList.toggle("is-on", b === band));
    if (band) { out("colour").textContent = band.colour; out("notes").textContent = band.notes; }
    const fact = facts.filter((f) => f.year <= y).pop();
    facts.forEach((f) => f.li.classList.toggle("is-on", f === fact));
    const exact = facts.find((f) => f.year === y);
    marker.innerHTML = exact ? $("span", exact.li).innerHTML : "";
    ticks.forEach((tk) => tk.classList.toggle("is-on", +tk.dataset.caskTick <= y));
    const m = [1 - Math.exp(-y / 6), 1 - Math.exp(-y / 4), 1 - Math.exp(-y / 8)];
    meters.forEach((b, i) => { b.style.transform = `scaleX(${m[i].toFixed(3)})`; });
    range.setAttribute("aria-valuetext", `${y} ${unit}, ${band ? band.colour : ""}, ${left.toFixed(1)} litres left of 100`);
  };
  range.addEventListener("input", update);
  $$('[data-cask-climate] input', sec).forEach((r) => r.addEventListener("change", () => {
    rate = (r.value === "hot" ? +sec.dataset.rateHot : +sec.dataset.rateCool) / 10; update();
  }));
  update();
  // A gentle demo sweep the first time the barrel is seen, so the reader knows it moves.
  // Triggered from scroll position like the rest of this file: an IntersectionObserver on
  // the range never fired reliably under Lenis-driven scrolling (seen 25 Sep 2026).
  if (!REDUCED && gsap) {
    let armed = true, touched = false;
    range.addEventListener("pointerdown", () => { touched = true; }, { once: true });
    const check = () => {
      if (!armed) return;
      const r = range.getBoundingClientRect();
      if (r.top > innerHeight * 0.85 || r.bottom < 0) return;
      armed = false; removeEventListener("scroll", check);
      if (touched || +range.value !== 0) return;
      const o = { v: 0 };
      gsap.to(o, { v: 7, duration: 2.4, ease: "power2.inOut", delay: 0.4, onUpdate: () => { if (touched) return; const v = Math.round(o.v); if (+range.value !== v) { range.value = v; update(); } } });
    };
    addEventListener("scroll", check, { passive: true });
    check();
  }
}

/* ---------- SA styles: tabs (all panels stay listed with JS off) ---------- */
function initStyles() {
  const sec = $("[data-psb-styles]");
  if (!sec) return;
  const list = $("[data-psb-tabs]", sec);
  const tabs = $$('[role="tab"]', list), panels = $$("[data-psb-panel]", sec);
  if (!tabs.length) return;
  list.hidden = false;
  const select = (i, focus) => {
    tabs.forEach((t, j) => { t.setAttribute("aria-selected", j === i); t.tabIndex = j === i ? 0 : -1; });
    panels.forEach((p, j) => { p.hidden = j !== i; p.classList.remove("is-shown"); });
    requestAnimationFrame(() => requestAnimationFrame(() => panels[i].classList.add("is-shown")));
    if (focus) tabs[i].focus();
  };
  tabs.forEach((t, i) => {
    t.addEventListener("click", () => select(i));
    t.addEventListener("keydown", (e) => {
      const k = e.key; let n = null;
      if (k === "ArrowRight") n = (i + 1) % tabs.length; else if (k === "ArrowLeft") n = (i - 1 + tabs.length) % tabs.length;
      else if (k === "Home") n = 0; else if (k === "End") n = tabs.length - 1;
      if (n != null) { e.preventDefault(); select(n, true); }
    });
  });
  panels.forEach((p, j) => { p.hidden = j !== 0; });
  new IntersectionObserver((ents, io) => { if (ents[0].isIntersecting) { io.disconnect(); panels.find((p) => !p.hidden).classList.add("is-shown"); } }, { threshold: 0.3 }).observe(sec);
}

/* ---------- timelines: sticky horizontal scroll on wide screens ---------- */
function initHistory() {
  const secs = $$("[data-psb-history]");
  if (!secs.length || REDUCED) return;
  const mq = matchMedia("(min-width: 990px)");
  const items = secs.map((sec) => ({ sec, outer: $("[data-psb-h-outer]", sec), track: $("[data-psb-h-track]", sec), bar: $("[data-psb-h-bar]", sec), dist: 0 }));
  const measure = () => {
    items.forEach((it) => {
      const vp = it.track.parentElement;
      if (!mq.matches) { it.sec.classList.remove("is-h"); it.outer.style.height = ""; it.track.style.transform = ""; it.dist = 0; vp.tabIndex = 0; return; }
      it.sec.classList.add("is-h"); vp.removeAttribute("tabindex");
      it.dist = Math.max(0, it.track.scrollWidth - document.documentElement.clientWidth);
      it.outer.style.height = `${innerHeight + it.dist}px`;
    });
    if (ST) ST.refresh();
    move();
  };
  const move = () => {
    items.forEach((it) => {
      if (!it.dist) return;
      const r = it.outer.getBoundingClientRect();
      const p = clamp(-r.top / Math.max(1, it.outer.offsetHeight - innerHeight), 0, 1);
      it.track.style.transform = `translate3d(${(-p * it.dist).toFixed(1)}px,0,0)`;
      if (it.bar) it.bar.style.transform = `scaleX(${p.toFixed(4)})`;
    });
  };
  // Keyboard users tabbing into an off-screen card: scroll the page to bring it in.
  items.forEach((it) => it.track.addEventListener("focusin", (e) => {
    if (!it.dist) return;
    const card = e.target.closest(".psb-event"); if (!card) return;
    const p = clamp((card.offsetLeft - innerWidth * 0.2) / it.dist, 0, 1);
    const top = scrollY + it.outer.getBoundingClientRect().top + p * (it.outer.offsetHeight - innerHeight);
    window.__lenis ? window.__lenis.scrollTo(top, { immediate: true }) : scrollTo(0, top);
  }));
  let ticking = false;
  addEventListener("scroll", () => { if (!ticking) { ticking = true; requestAnimationFrame(() => { ticking = false; move(); }); } }, { passive: true });
  let rz; addEventListener("resize", () => { clearTimeout(rz); rz = setTimeout(measure, 150); });
  mq.addEventListener("change", measure);
  measure();
  addEventListener("load", measure, { once: true });
}

/* ---------- the page "thermometer": progress + current chapter ---------- */
function initGauge() {
  const psb = $$(".psb");
  if (psb.length < 2) return;
  const heads = $$(".psb h2");
  const g = document.createElement("div");
  g.className = "psb psb-gauge"; g.setAttribute("aria-hidden", "true");
  g.innerHTML = '<p class="psb-gauge__label"></p><div class="psb-gauge__tube"><i></i></div><div class="psb-gauge__bulb"></div>';
  document.body.appendChild(g);
  const fillEl = $("i", g), lab = $(".psb-gauge__label", g);
  let ticking = false;
  const upd = () => {
    ticking = false;
    const a = psb[0].getBoundingClientRect().top + scrollY + innerHeight * 0.8;
    const b = psb[psb.length - 1].getBoundingClientRect().bottom + scrollY - innerHeight;
    const p = clamp((scrollY - a) / Math.max(1, b - a), 0, 1);
    g.classList.toggle("is-on", scrollY > a && scrollY < b + innerHeight * 0.5);
    fillEl.style.transform = `scaleY(${p.toFixed(4)})`;
    let cur = null; heads.forEach((h) => { if (h.getBoundingClientRect().top < innerHeight * 0.5) cur = h; });
    const t = cur ? cur.textContent.trim() : "";
    if (lab.textContent !== t) lab.textContent = t;
  };
  addEventListener("scroll", () => { if (!ticking) { ticking = true; requestAnimationFrame(upd); } }, { passive: true });
  upd();
}

/* ==========================================================================
   MYTH OR HISTORY? — guess, flip, score, share
   ========================================================================== */
function initMyths() {
  const sec = $("[data-psb-myths]");
  if (!sec) return;
  const cards = $$("[data-psb-myth]", sec);
  if (!cards.length) return;
  const d = sec.dataset;
  const scoreEl = $("[data-myth-score]", sec), progress = $("[data-myth-progress]", sec);
  const end = $("[data-myth-end]", sec), finalEl = $("[data-myth-final]", sec), status = $("[data-myth-status]", sec);
  const baseProgress = progress.textContent;
  const state = cards.map(() => ({ guess: null, right: false }));
  const pageUrl = (hash) => location.origin + location.pathname + (hash ? "#" + hash : "");
  const say = (m) => { status.textContent = ""; requestAnimationFrame(() => { status.textContent = m; }); };
  const fill = (tpl, map) => Object.keys(map).reduce((t, k) => t.split(`[${k}]`).join(map[k]), tpl || "");

  // Only the visible face is reachable by keyboard and screen reader.
  const setFaces = (card, flipped) => {
    const front = $("[data-myth-front]", card), back = $("[data-myth-back]", card);
    front.inert = flipped; back.inert = !flipped;
    front.toggleAttribute("aria-hidden", flipped); back.toggleAttribute("aria-hidden", !flipped);
  };
  const flip = (card, on, focus) => {
    card.classList.toggle("is-flipped", on);
    setFaces(card, on);
    if (focus) (on ? $("[data-myth-stamp]", card) : $("[data-myth-guess] button:not(:disabled)", card) || $(".psb-myth__claim", card)).focus({ preventScroll: true });
  };
  const score = () => state.filter((x) => x.right).length;
  const answered = () => state.filter((x) => x.guess).length;
  const render = () => {
    scoreEl.textContent = score();
    progress.textContent = answered() < cards.length ? `${baseProgress} · ${answered()} / ${cards.length}` : baseProgress;
    if (answered() === cards.length) {
      const f = score() / cards.length;
      finalEl.textContent = `${score()} / ${cards.length}. ${f >= 0.9 ? d.f1 : f >= 0.6 ? d.f2 : f >= 0.3 ? d.f3 : d.f4}`;
      end.hidden = false;
    } else end.hidden = true;
  };

  // Enhance: show the guess buttons and the scoreboard, start every card face-up on the story.
  $("[data-myth-scoreboard]", sec).hidden = false;
  cards.forEach((card, i) => {
    $("[data-myth-guess]", card).hidden = false;
    $("[data-myth-actions]", card).hidden = false;
    setFaces(card, false);
    $$("[data-guess]", card).forEach((btn) => btn.addEventListener("click", () => {
      if (state[i].guess) { flip(card, true, true); return; }
      const g = btn.dataset.guess, ans = card.dataset.answer;
      state[i].guess = g;
      state[i].right = ans === "either" || ans === g;
      $$("[data-guess]", card).forEach((b) => { b.setAttribute("aria-pressed", b === btn); b.disabled = b !== btn; });
      const label = g === "myth" ? d.lMyth : d.lHistory;
      const res = $("[data-myth-result]", card);
      res.textContent = fill(ans === "either" ? d.lEither : state[i].right ? d.lRight : d.lWrong, { guess: label });
      res.className = "psb-myth__result " + (state[i].right ? "is-right" : "is-wrong");
      flip(card, true, true);
      render();
      say(`${res.textContent} ${$("[data-myth-stamp]", card).textContent.trim()}.`);
    }));
    $("[data-myth-flipback]", card).addEventListener("click", () => flip(card, false, true));
    $("[data-myth-share-card]", card).addEventListener("click", () => {
      const claim = $(".psb-myth__claim", card).textContent.trim().replace(/^“|”$/g, "");
      share(fill(d.cardShare, { claim, verdict: $("[data-myth-stamp]", card).textContent.trim() }), pageUrl(card.id));
    });
  });

  async function share(text, url) {
    if (navigator.share) {
      try { await navigator.share({ title: document.title, text, url }); return; }
      catch (e) { if (e && e.name === "AbortError") return; }
    }
    try { await navigator.clipboard.writeText(`${text} ${url}`); say(d.lCopied); }
    catch (e) { window.prompt("Copy this link", `${text} ${url}`); }
  }
  $("[data-myth-share]", sec).addEventListener("click", () => share(fill(d.shareText, { score: score(), total: cards.length }), pageUrl(sec.id)));
  $("[data-myth-reset]", sec).addEventListener("click", () => {
    state.forEach((x) => { x.guess = null; x.right = false; });
    cards.forEach((card) => {
      $$("[data-guess]", card).forEach((b) => { b.disabled = false; b.removeAttribute("aria-pressed"); });
      $("[data-myth-result]", card).textContent = "";
      flip(card, false, false);
    });
    render();
    const first = $("[data-guess]", cards[0]);
    cards[0].scrollIntoView({ behavior: REDUCED ? "auto" : "smooth", block: "center" });
    first.focus({ preventScroll: true });
  });

  // A shared link to one story (#myth-3) opens with that card already turned.
  const m = location.hash.match(/^#myth-(\d+)$/);
  if (m && cards[+m[1] - 1]) flip(cards[+m[1] - 1], true, false);
  render();
}

/* ---------- first releases: the spine fills, each node lights as it passes mid-screen ---------- */
function initLineage() {
  const sec = $("[data-psb-lineage]");
  if (!sec) return;
  const fill = $("[data-lineage-fill]", sec), items = $$("[data-lineage-item]", sec);
  if (!fill || !items.length) return;
  if (REDUCED) { fill.style.transform = "scaleY(1)"; items.forEach((it) => it.classList.add("is-lit")); return; }
  const body = fill.parentElement.parentElement;
  const nodes = items.map((it) => $(".psb-release__node", it));
  let ticking = false;
  const upd = () => {
    ticking = false;
    const mid = innerHeight * 0.55, r = body.getBoundingClientRect();
    fill.style.transform = `scaleY(${clamp((mid - r.top) / Math.max(1, r.height), 0, 1).toFixed(4)})`;
    items.forEach((it, i) => { const n = nodes[i].getBoundingClientRect(); it.classList.toggle("is-lit", n.top + n.height / 2 < mid); });
  };
  addEventListener("scroll", () => { if (!ticking) { ticking = true; requestAnimationFrame(upd); } }, { passive: true });
  addEventListener("resize", upd);
  upd();
}

/* ==========================================================================
   BRANDY DISCOVERY — the homepage Product journey, rebuilt for this page.
   ≥1024px with motion: the stage pins and page scroll carries the track one bottle
   at a time, with a gentle parallax between copy, bottle and glow. As it moves, the
   years-in-oak marker slides between the bottles' ages and --spirit follows the
   barrel ramp, so the cellar light deepens with the brandy. Reduced motion: a native
   scroll-snap strip that still drives the scale. Phones: a plain stack.
   ========================================================================== */
function initDiscovery() {
  const sec = $("[data-psb-disc]");
  if (!sec) return;
  const rail = $("[data-disc-rail]", sec), main = $("[data-disc-main]", sec), track = $("[data-disc-track]", sec);
  const slides = $$("[data-disc-slide]", sec);
  if (!rail || !main || !track || !slides.length) return;
  const n = slides.length, max = +sec.dataset.max || 20, unit = sec.dataset.unit || "";
  const years = slides.map((s) => +s.dataset.years || 0);
  const bar = $(".psb-disc__bar", sec), ageEl = $("[data-disc-age]", sec), countEm = $("[data-disc-count] em", sec);
  const prev = $("[data-disc-prev]", sec), next = $("[data-disc-next]", sec);
  const layers = slides.map((el) => ({ el, text: $(".psb-dslide__text", el), media: $(".psb-dslide__media", el), glow: $(".psb-dslide__glow", el) }));
  const mq = matchMedia("(min-width: 1024px)");
  const P_TEXT = 64, P_MEDIA = -30, P_GLOW = -88;
  let pin = false, ovr = 0, slideW = 1, active = -1;

  // f = fractional slide index (1.5 = halfway from the second bottle to the third)
  const setAge = (f) => {
    f = clamp(f, 0, n - 1);
    const i = Math.floor(f), j = Math.min(n - 1, i + 1);
    const y = lerp(years[i], years[j], f - i);
    sec.style.setProperty("--spirit", spiritAt(y));
    if (bar) bar.style.setProperty("--p", `${clamp((y / max) * 100, 0, 100).toFixed(2)}%`);
    if (ageEl) ageEl.textContent = `${Math.round(y)} ${unit}`;
    const a = Math.round(f);
    if (a === active) return;
    active = a;
    slides.forEach((s, x) => s.classList.toggle("is-active", x === a));
    if (countEm) countEm.textContent = String(a + 1).padStart(2, "0");
    if (prev) prev.disabled = a <= 0;
    if (next) next.disabled = a >= n - 1;
  };

  const measure = () => {
    sec.style.setProperty("--slide-w", `${main.clientWidth}px`);
    slideW = slides[0].offsetWidth || main.clientWidth || 1;
    ovr = Math.max(0, track.scrollWidth - main.clientWidth);
    rail.style.height = pin ? `${innerHeight + ovr}px` : "";
  };
  const parallax = (tx) => {
    const w = main.clientWidth || 1;
    layers.forEach((L) => {
      const d = (L.el.offsetLeft + tx) / w;
      if (L.text) L.text.style.transform = `translate3d(${(d * P_TEXT).toFixed(1)}px,0,0)`;
      if (L.media) L.media.style.transform = `translate3d(${(d * P_MEDIA).toFixed(1)}px,0,0)`;
      if (L.glow) L.glow.style.transform = `translate3d(${(d * P_GLOW).toFixed(1)}px,0,0)`;
      L.el.style.setProperty("--focus", Math.max(0, 1 - Math.abs(d) * 1.6).toFixed(3));
    });
  };
  const clearLayers = () => layers.forEach((L) => {
    [L.text, L.media, L.glow].forEach((e) => { if (e) e.style.transform = ""; });
    L.el.style.removeProperty("--focus");
  });
  const apply = () => {
    if (!pin) return;
    const p = ovr ? clamp(-rail.getBoundingClientRect().top / ovr, 0, 1) : 0;
    const tx = -p * ovr;
    track.style.transform = `translate3d(${tx.toFixed(1)}px,0,0)`;
    parallax(tx);
    setAge(p * (n - 1));
  };

  const goTo = (i) => {
    i = clamp(i, 0, n - 1);
    if (pin) {
      const top = scrollY + rail.getBoundingClientRect().top + (n > 1 ? (i / (n - 1)) * ovr : 0);
      window.__lenis ? window.__lenis.scrollTo(top, { duration: 0.9 }) : scrollTo({ top, behavior: "smooth" });
    } else if (mq.matches) main.scrollTo({ left: slides[i].offsetLeft, behavior: REDUCED ? "auto" : "smooth" });
    else slides[i].scrollIntoView({ block: "center", behavior: REDUCED ? "auto" : "smooth" });
  };
  if (prev) prev.addEventListener("click", () => goTo(active - 1));
  if (next) next.addEventListener("click", () => goTo(active + 1));
  main.addEventListener("keydown", (e) => {
    if (e.target !== main) return;
    if (e.key === "ArrowRight") { e.preventDefault(); goTo(active + 1); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); goTo(active - 1); }
  });
  // Tabbing to a link on an off-screen bottle brings that bottle into view.
  track.addEventListener("focusin", (e) => {
    const s = e.target.closest("[data-disc-slide]");
    if (s && pin) { const i = slides.indexOf(s); if (i !== active) goTo(i); }
  });

  // Not pinned: the native strip (reduced motion) or the phone stack still drive the scale.
  main.addEventListener("scroll", () => { if (!pin && mq.matches) setAge(main.scrollLeft / slideW); }, { passive: true });
  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver((ents) => ents.forEach((en) => {
      if (en.isIntersecting && !mq.matches) setAge(slides.indexOf(en.target));
    }), { rootMargin: "-45% 0px -45% 0px" });
    slides.forEach((s) => io.observe(s));
    // The section carries its own years-in-oak scale, so the theme's scroll thread and the
    // page gauge step aside while it holds the middle of the screen.
    new IntersectionObserver((ents) => ents.forEach((en) => {
      document.body.classList.toggle("psb-disc-active", en.isIntersecting);
    }), { rootMargin: "-50% 0px -50% 0px" }).observe(rail);
  }

  let ticking = false;
  addEventListener("scroll", () => { if (pin && !ticking) { ticking = true; requestAnimationFrame(() => { ticking = false; apply(); }); } }, { passive: true });
  const decide = () => {
    const want = mq.matches && !REDUCED;
    if (want !== pin) {
      pin = want;
      sec.classList.toggle("is-pin", pin);
      if (!pin) { track.style.transform = ""; clearLayers(); }
    }
    measure();
    if (pin) apply(); else setAge(mq.matches ? main.scrollLeft / slideW : Math.max(0, active));
    if (ST) ST.refresh();
  };
  let rz; addEventListener("resize", () => { clearTimeout(rz); rz = setTimeout(decide, 150); });
  mq.addEventListener("change", decide);
  decide();
  addEventListener("load", decide, { once: true });
}

/* ---------- boot: each piece fails alone ---------- */
[initReveals, initTitle, initMorph, initProcess, initCut, initBarrel, initStyles, initHistory, initLineage, initDiscovery, initMyths, initGauge].forEach((fn) => {
  try { fn(); } catch (e) { console.error("[psb]", fn.name, e); }
});
initStill().catch((e) => console.error("[psb] still", e));
